import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import SendIcon from "@mui/icons-material/Send";
import {
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useClickOutside from "../../../hooks/useClickOutside";
import strings from "../../../strings";
import { formatFileSize } from "../../../utils/messageChat";
import useAutosizeTextArea from "../../../utils/useAutosizeTextArea";
import {
  TextChatAttachmentPolicy,
  TextChatAvailability,
  TextChatCapabilities,
} from "../TextChat.types";
import s from "./TextChatComposer.module.css";

type TextChatComposerLabels = {
  input: string;
  send: string;
  attach: string;
  fileInput: string;
  emoji: string;
  mentionSuggestions: string;
  selectedFiles: string;
  removeFile: (fileName: string) => string;
  fileTooLarge: (maxFileSizeBytes: number) => string;
  tooManyFiles: (maxFiles: number) => string;
  unsupportedType: string;
  audioUnsupported: string;
  unavailable: string;
};

export type TextChatComposerSubmitPayload = {
  text: string;
  files: File[];
};

export type TextChatMentionCandidate = {
  id: string;
  label: string;
};

type TextChatComposerProps = {
  text: string;
  selectedFiles: File[];
  capabilities: Pick<TextChatCapabilities, "attachments" | "emojiPicker">;
  attachmentPolicy: TextChatAttachmentPolicy;
  availability: TextChatAvailability;
  onTextChange: (text: string) => void;
  onSelectedFilesChange: (files: File[]) => void;
  onSend: (payload: TextChatComposerSubmitPayload) => Promise<void> | void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  labels?: Partial<TextChatComposerLabels>;
  isSending?: boolean;
  maxTextAreaHeight?: number;
  mentionCandidates?: TextChatMentionCandidate[];
  selectedMentionIds?: string[];
  onMentionSelect?: (mention: TextChatMentionCandidate) => void;
  hasSendableContent?: boolean;
  contextualAction?: ReactNode;
  isCompositionLocked?: boolean;
  defaultTheme?: "light" | "dark";
};

const defaultLabels: TextChatComposerLabels = {
  input: strings.typeMessagePlaceholder,
  send: strings.globalChatSendMessage,
  attach: strings.globalChatAttachFile,
  fileInput: strings.globalChatAttachFileInput,
  emoji: strings.globalChatToggleEmojiPicker,
  mentionSuggestions: strings.globalChatMentionSuggestions,
  selectedFiles: strings.globalChatSelectedFiles,
  removeFile: (fileName) =>
    strings.formatString("globalChatRemoveFile", { name: fileName }) as string,
  fileTooLarge: (maxFileSizeBytes) =>
    strings.formatString(strings.fileSizeMoreThan, {
      size: Math.round(maxFileSizeBytes / 1024 / 1024),
    }) as string,
  tooManyFiles: (maxFiles) =>
    strings.formatString("globalChatTooManyFiles", {
      count: maxFiles,
    }) as string,
  unsupportedType: strings.globalChatUnsupportedFileType,
  audioUnsupported: strings.globalChatAudioAttachmentUseVoiceControls,
  unavailable: strings.textChatUnavailable,
};

const getFileExtension = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf(".");

  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";
};

const quickEmojis = ["👍", "❤️", "😂", "🙂", "🙏", "🔥", "👏", "🎉"];

const isFileTypeAccepted = (
  file: File,
  attachmentPolicy: TextChatAttachmentPolicy,
) => {
  if (
    !attachmentPolicy.acceptedMimeTypes.length &&
    !attachmentPolicy.acceptedFileExtensions.length
  ) {
    return true;
  }

  return (
    attachmentPolicy.acceptedMimeTypes.some((acceptedType) => {
      if (acceptedType.endsWith("/*")) {
        return file.type.startsWith(acceptedType.replace("/*", "/"));
      }

      return file.type === acceptedType;
    }) ||
    attachmentPolicy.acceptedFileExtensions.includes(
      getFileExtension(file.name),
    )
  );
};

const SelectedFilePreview = ({
  file,
  onRemove,
  removeLabel,
}: {
  file: File;
  onRemove: () => void;
  removeLabel: string;
}) => {
  const isImage = file.type.startsWith("image/");
  const previewUrl = useMemo(
    () => (isImage ? URL.createObjectURL(file) : ""),
    [file, isImage],
  );

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <li className={s.filePreviewItem}>
      <div className={s.filePreviewThumb}>
        {isImage ? (
          <img src={previewUrl} alt={file.name} />
        ) : (
          <span className={s.filePreviewExt}>
            {getFileExtension(file.name).replace(".", "").toUpperCase() ||
              "FILE"}
          </span>
        )}
      </div>
      <div className={s.filePreviewInfo}>
        <span className={s.filePreviewName} title={file.name}>
          {file.name}
        </span>
        <span className={s.filePreviewMeta}>{formatFileSize(file.size)}</span>
      </div>
      <button
        type="button"
        className={s.removeFileButton}
        aria-label={removeLabel}
        onClick={onRemove}
      >
        <CloseIcon fontSize="small" />
      </button>
    </li>
  );
};

const TextChatComposer = ({
  text,
  selectedFiles,
  capabilities,
  attachmentPolicy,
  availability,
  onTextChange,
  onSelectedFilesChange,
  onSend,
  onFocus,
  onBlur,
  placeholder,
  labels,
  isSending = false,
  maxTextAreaHeight = 200,
  mentionCandidates = [],
  selectedMentionIds = [],
  onMentionSelect,
  hasSendableContent,
  contextualAction,
  isCompositionLocked = false,
  defaultTheme,
}: TextChatComposerProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const mergedLabels = useMemo(
    () => ({
      ...defaultLabels,
      ...labels,
    }),
    [labels],
  );
  const canAttach = capabilities.attachments && attachmentPolicy.enabled;

  const canUseEmoji = capabilities.emojiPicker;
  const canWrite = availability.canWrite && !isSending && !isCompositionLocked;
  const hasContent =
    hasSendableContent ?? (text.trim().length > 0 || selectedFiles.length > 0);
  const canSend = canWrite && hasContent;
  const shouldShowSendButton = hasContent || !contextualAction;
  const mentionQueryMatch = text.match(/(?:^|\s)@([^\s@]*)$/);
  const isMentionQueryActive = Boolean(mentionQueryMatch);
  const mentionQuery = mentionQueryMatch?.[1].toLowerCase() ?? "";
  const filteredMentionCandidates = useMemo(
    () =>
      isMentionQueryActive
        ? mentionCandidates
            .filter((candidate) => !selectedMentionIds.includes(candidate.id))
            .filter((candidate) =>
              candidate.label.toLowerCase().includes(mentionQuery),
            )
            .slice(0, 6)
        : [],
    [isMentionQueryActive, mentionCandidates, mentionQuery, selectedMentionIds],
  );
  const acceptedFileTypes = [
    ...attachmentPolicy.acceptedMimeTypes,
    ...attachmentPolicy.acceptedFileExtensions,
  ].join(", ");

  useAutosizeTextArea(textAreaRef.current, text);
  useClickOutside(emojiPickerRef, () => setIsEmojiPickerOpen(false));

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.maxHeight = `${maxTextAreaHeight}px`;
    }
  }, [maxTextAreaHeight]);

  const appendFiles = (files: File[]) => {
    setValidationMessage("");

    if (!canAttach || !files.length) {
      return;
    }

    if (selectedFiles.length + files.length > attachmentPolicy.maxFiles) {
      setValidationMessage(
        mergedLabels.tooManyFiles(attachmentPolicy.maxFiles),
      );
      return;
    }

    const unsupportedFile = files.find(
      (file) => !isFileTypeAccepted(file, attachmentPolicy),
    );

    if (unsupportedFile) {
      setValidationMessage(mergedLabels.unsupportedType);
      return;
    }

    const oversizedFile = files.find(
      (file) => file.size > attachmentPolicy.maxFileSizeBytes,
    );

    if (oversizedFile) {
      setValidationMessage(
        mergedLabels.fileTooLarge(attachmentPolicy.maxFileSizeBytes),
      );
      return;
    }

    onSelectedFilesChange([...selectedFiles, ...files]);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    appendFiles(Array.from(event.target.files || []));
    event.target.value = "";
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!attachmentPolicy.allowPaste || !canAttach) {
      return;
    }

    const files = Array.from(event.clipboardData.items)
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())
      .filter((file): file is File => Boolean(file));

    if (files.length) {
      appendFiles(files);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!attachmentPolicy.allowDrop || !canAttach) {
      return;
    }

    const files = Array.from(event.dataTransfer.files || []);
    if (!files.length) {
      return;
    }

    event.preventDefault();
    appendFiles(files);
  };

  const handleSend = async () => {
    if (!canSend) {
      return;
    }

    setValidationMessage("");
    await onSend({ text, files: selectedFiles });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleEmojiClick = (emoji: string) => {
    onTextChange(`${text}${emoji}`);
    textAreaRef.current?.focus();
  };

  const removeFile = (index: number) => {
    onSelectedFilesChange(
      selectedFiles.filter((_, fileIndex) => fileIndex !== index),
    );
  };

  const availabilityMessage =
    availability.status === "available"
      ? ""
      : availability.messageKey
        ? strings[availability.messageKey] || mergedLabels.unavailable
        : mergedLabels.unavailable;

  return (
    <div
      className={s.container}
      data-theme={defaultTheme}
      onDrop={handleDrop}
      onDragOver={(event) => {
        if (attachmentPolicy.allowDrop && canAttach) {
          event.preventDefault();
        }
      }}
    >
      {selectedFiles.length > 0 && (
        <ul
          className={s.filePreviewList}
          aria-label={mergedLabels.selectedFiles}
        >
          {selectedFiles.map((file, index) => (
            <SelectedFilePreview
              key={`${file.name}-${file.size}-${index}`}
              file={file}
              onRemove={() => removeFile(index)}
              removeLabel={mergedLabels.removeFile(file.name)}
            />
          ))}
        </ul>
      )}

      {(validationMessage || availabilityMessage) && (
        <div className={s.statusMessage} role="status">
          {validationMessage || availabilityMessage}
        </div>
      )}

      {canWrite && onMentionSelect && filteredMentionCandidates.length > 0 && (
        <div
          className={s.mentionSuggestions}
          aria-label={mergedLabels.mentionSuggestions}
        >
          {filteredMentionCandidates.map((candidate) => (
            <button
              type="button"
              key={candidate.id}
              className={s.mentionSuggestionButton}
              onClick={() => {
                onMentionSelect(candidate);
                textAreaRef.current?.focus();
              }}
            >
              @{candidate.label}
            </button>
          ))}
        </div>
      )}

      <div
        className={`${s.composer} ${
          isCompositionLocked ? s.composerLocked : ""
        }`}
      >
        {canAttach ? (
          <>
            <button
              type="button"
              className={`${s.iconButton} ${s.composerControl} ${s.attachControl}`}
              aria-label={mergedLabels.attach}
              hidden={isCompositionLocked}
              onClick={() => fileInputRef.current?.click()}
              disabled={!availability.canWrite || isCompositionLocked}
            >
              <AttachFileIcon fontSize="small" />
            </button>
            <input
              ref={fileInputRef}
              className={s.fileInput}
              type="file"
              accept={acceptedFileTypes}
              aria-label={mergedLabels.fileInput}
              multiple={attachmentPolicy.maxFiles > 1}
              onChange={handleFileChange}
            />
          </>
        ) : (
          <div
            className={`${s.composerControl} ${s.attachControl}`}
            hidden={isCompositionLocked}
          />
        )}
        <textarea
          ref={textAreaRef}
          className={`${s.textarea} ${s.composerControl}`}
          hidden={isCompositionLocked}
          value={text}
          placeholder={placeholder || mergedLabels.input}
          aria-label={mergedLabels.input}
          onChange={(event) => onTextChange(event.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          rows={1}
          disabled={!availability.canWrite || isCompositionLocked}
        />
        <div className={s.actions}>
          {canUseEmoji && (
            <button
              type="button"
              className={`${s.iconButton} ${s.composerControl}`}
              aria-label={mergedLabels.emoji}
              hidden={isCompositionLocked}
              aria-expanded={isEmojiPickerOpen}
              onClick={() => setIsEmojiPickerOpen((prev) => !prev)}
              disabled={!availability.canWrite || isCompositionLocked}
            >
              <InsertEmoticonIcon fontSize="small" />
            </button>
          )}

          {shouldShowSendButton ? (
            <button
              type="button"
              className={`${s.sendButton} ${s.composerControl}`}
              aria-label={mergedLabels.send}
              hidden={isCompositionLocked}
              onClick={handleSend}
              disabled={!canSend}
            >
              {isSending ? (
                <span className={s.spinner} role="status">
                  <span className={s.srOnly}>{strings.sending}</span>
                </span>
              ) : (
                <SendIcon fontSize="small" />
              )}
            </button>
          ) : (
            contextualAction
          )}
        </div>
      </div>

      {isEmojiPickerOpen && (
        <div className={s.emojiPicker} ref={emojiPickerRef}>
          <div className={s.quickEmojiPicker}>
            {quickEmojis.map((emoji) => (
              <button
                type="button"
                key={emoji}
                className={s.quickEmojiButton}
                onClick={() => handleEmojiClick(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TextChatComposer;
